// `CancellationBanner` — inline banner shown on a cancelled relance row
// (US-164).
//
// Maps a `cancelled_*` row state (or a transient `relance.cancelled` realtime
// event) to a localised reason string + tone. The banner replaces the action
// row so users can't accidentally fire a relance the system already
// reconciled. The compact variant is what the pending-queue uses; the full
// variant is reserved for a future "history" surface (out of scope for
// W5d.3).

import { useT } from '@barel/i18n';
import { Banner } from '@barel/ui';

export type CancellationReason =
  | 'manual_reply'
  | 'lead_replied'
  | 'user_action'
  | 'campaign_paused';

export interface CancellationBannerProps {
  reason: CancellationReason;
  'data-testid'?: string;
}

const REASON_TONE: Record<CancellationReason, 'info' | 'warning' | 'success'> = {
  manual_reply: 'success',
  lead_replied: 'success',
  user_action: 'info',
  campaign_paused: 'warning',
};

const REASON_KEY: Record<CancellationReason, string> = {
  manual_reply: 'queue.cancellation.manualReply',
  lead_replied: 'queue.cancellation.leadReplied',
  user_action: 'queue.cancellation.userAction',
  campaign_paused: 'queue.cancellation.campaignPaused',
};

/** Map a `relancesEmitted.state` to a `CancellationReason` if applicable. */
export function reasonFromState(state: string | null | undefined): CancellationReason | null {
  switch (state) {
    case 'cancelled_manual_reply':
      return 'manual_reply';
    case 'cancelled_user':
      return 'user_action';
    default:
      // Any other `cancelled_*` state still means the system reconciled the
      // row — it must NOT stay actionable. Fall back to a generic non-null
      // reason so `RelanceRow` always swaps the action cluster for the banner.
      // (`RELANCE_EMITTED_STATES` only declares the two states above today;
      // this guards against a new `cancelled_*` variant silently re-exposing
      // Send/Skip/Snooze on a row the backend already cancelled.)
      return typeof state === 'string' && state.startsWith('cancelled_') ? 'user_action' : null;
  }
}

export function CancellationBanner({ reason, ...rest }: CancellationBannerProps) {
  const t = useT('relances');
  const message = t(REASON_KEY[reason]);
  return (
    <Banner
      tone={REASON_TONE[reason]}
      variant="soft"
      data-testid={rest['data-testid'] ?? 'relance-cancellation-banner'}
      data-reason={reason}
    >
      {message}
    </Banner>
  );
}
